import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, MapPin, Phone, Mail } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { toMapEmbed } from "@/lib/maps";
import { externalUrl } from "@/lib/url";

const quick = [
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products" },
  { to: "/machine-locations", label: "Machine Locations" },
  { to: "/find-distributor", label: "Find Distributor" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact Us" },
];

export default function Footer() {
  const { settings } = useSite();
  return (
    <footer className="bg-brand-navy text-blue-100" data-testid="site-footer">
      <div className="container-page grid grid-cols-1 gap-10 py-16 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="flex items-center gap-2.5">
            <span className="grid h-9 w-9 place-items-center rounded-lg bg-white font-display text-lg font-800 text-brand">A</span>
            <span className="font-display text-lg font-800 tracking-tight text-white">AIMWAY</span>
          </div>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-blue-200/80">{settings.about_short || settings.tagline}</p>
          <div className="mt-6 flex gap-3">
            {[[Facebook, settings.facebook, "facebook"], [Instagram, settings.instagram, "instagram"], [Linkedin, settings.linkedin, "linkedin"]]
              .map(([Icon, raw, name]) => [Icon, externalUrl(raw), name])
              .filter(([, url]) => !!url)
              .map(([Icon, url, name], i) => (
                <a key={name} href={url} target="_blank" rel="noreferrer noopener" className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-white transition-colors hover:bg-brand-orange hover:border-brand-orange" data-testid={`footer-social-${name}`} aria-label={name}>
                  <Icon size={16} />
                </a>
              ))}
          </div>
        </div>

        <div>
          <h4 className="font-display text-sm font-700 uppercase tracking-wider text-white">Quick Links</h4>
          <ul className="mt-5 space-y-3 text-sm">
            {quick.map((q) => (
              <li key={q.to}><Link to={q.to} className="text-blue-200/80 transition-colors hover:text-brand-orange">{q.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-700 uppercase tracking-wider text-white">Get in Touch</h4>
          <ul className="mt-5 space-y-4 text-sm">
            <li className="flex gap-3"><MapPin size={18} className="mt-0.5 shrink-0 text-brand-orange" /><span className="text-blue-200/80">{settings.address}</span></li>
            <li className="flex gap-3"><Phone size={18} className="shrink-0 text-brand-orange" /><a href={`tel:${(settings.phone||"").replace(/\s+/g,"")}`} className="text-blue-200/80 hover:text-white">{settings.phone}</a></li>
            <li className="flex gap-3"><Mail size={18} className="shrink-0 text-brand-orange" /><a href={`mailto:${settings.email}`} className="text-blue-200/80 hover:text-white">{settings.email}</a></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-sm font-700 uppercase tracking-wider text-white">Location</h4>
          <div className="mt-5 overflow-hidden rounded-xl border border-white/10">
            <iframe title="map" src={toMapEmbed(settings.map_embed, settings.address)} className="h-40 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-page flex flex-col items-center justify-between gap-3 py-6 sm:flex-row">
          <p className="text-sm text-blue-200/70">© {new Date().getFullYear()} {settings.company_name}. All rights reserved.</p>
          <div className="flex items-center gap-4 text-sm text-blue-200/70">
            <span>Made in India</span>
            <Link to="/admin/login" className="opacity-60 transition-opacity hover:opacity-100" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
